import { Section } from '~/components/layout';
import { ButtonLink, SmartImage } from '~/components/ui';
import {
  payrollClosing as content,
  payrollOfferCta,
  payrollTermsNote,
  payrollYcCta,
} from '~/content/payroll';

/**
 * "Ready to make payroll free?" — numbered steps in a white card over a
 * full-bleed photo on desktop (Figma 1176:33826); photo stacked above the
 * steps on mobile (Figma 1269:47457).
 */
export function PayrollClosing({ className }: { className?: string }) {
  return (
    <Section className={className}>
      <div className="relative lg:min-h-[733px] lg:py-[22px] lg:ps-6">
        <div className="relative aspect-square overflow-hidden lg:absolute lg:inset-0 lg:aspect-auto">
          <SmartImage image={content.image} className="absolute inset-0 size-full" />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage:
                'linear-gradient(96deg, rgba(0, 0, 0, 0.2) 23.8%, rgba(255, 255, 255, 0) 97.1%)',
            }}
          />
        </div>

        <div className="relative mt-[52px] bg-white lg:mt-0 lg:w-[511px] lg:p-5">
          <div className="border-b border-black-6 pb-8">
            <h2 className="max-w-[293px] text-h3 leading-[1.16] font-semibold tracking-[-0.704px] text-grey-1000 lg:text-h2 lg:tracking-[-0.88px]">
              {content.title}
            </h2>
          </div>
          <ol className="mt-12 flex flex-col gap-[30px] lg:mt-[45px] lg:gap-8">
            {content.steps.map((step, i) => (
              <li key={step.title} className="flex items-start gap-5">
                {/* The <ol> already announces the step number. */}
                <span
                  aria-hidden
                  className="flex size-12 shrink-0 items-center justify-center rounded-full bg-black-6 text-body text-grey-1000 lg:font-semibold">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex flex-col gap-4 py-1.5 lg:gap-5">
                  <h3 className="text-body leading-[1.16] font-semibold text-grey-1000">
                    {step.title}
                  </h3>
                  <p className="text-body-m leading-[1.32] tracking-[0.27px] text-grey-900 lg:text-body lg:tracking-[-0.2px]">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10 flex flex-wrap gap-4 lg:mt-[45px]">
            <ButtonLink href={payrollYcCta.href} external={payrollYcCta.external}>
              {payrollYcCta.label}
            </ButtonLink>
            <ButtonLink
              href={payrollOfferCta.href}
              external={payrollOfferCta.external}
              variant="secondary">
              {payrollOfferCta.label}
            </ButtonLink>
          </div>
          <p className="mt-10 text-body text-grey-500 lg:mt-[30px]">{payrollTermsNote}</p>
        </div>
      </div>
    </Section>
  );
}
